import {
  Badge,
  Box,
  Button,
  Grid,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { CustomEvent } from "schemas/functions";

import { PAGE_VISITED } from "constants/trackingProps";
import CampaignBuilderDialog from "features/Influencer/Campaigns/CampaignBuilder/CampaignBuilderDialog";
import { CampaignStep } from "features/Influencer/Campaigns/CampaignBuilder/schema";
import { trackEvent } from "utils/tracking";
import { usePageSize } from "utils/usePageSize";

import BrandVerificationDialog from "./BrandVerificationDialog";
import CheckoutDialog from "./CheckoutDialog";
import CompletedDialog from "./CompletedDialog";
import LocationDialog from "./LocationDialog";
import { MapListItem } from "./MapListItem";
import OnboardingLocal from "./OnboardingLocal";
import LocalPitchingPreviewPage from "./PreviewPage";
import { GooglePlaceBrand, GooglePlaceResult } from "./schema";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_JS_KEY || "";
const libraries: "places"[] = ["places"];

const mapOptions: google.maps.MapOptions = {
  styles: [
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
  ],
  mapTypeControl: false,
};

export const PLACE_FIELDS = [
  "id",
  "displayName",
  "location",
  "photos",
  "rating",
  "userRatingCount",
  "formattedAddress",
  "priceLevel",
  "primaryTypeDisplayName",
  "websiteURI",
  "googleMapsURI",
  "businessStatus",
  "internationalPhoneNumber",
  "addressComponents",
];

export const splitBoundsIntoQuadrants = (bounds: google.maps.LatLngBounds) => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const midLat = (ne.lat() + sw.lat()) / 2;
  const midLng = (ne.lng() + sw.lng()) / 2;

  return [
    new google.maps.LatLngBounds(
      { lat: midLat, lng: sw.lng() },
      { lat: ne.lat(), lng: midLng },
    ),
    new google.maps.LatLngBounds(
      { lat: midLat, lng: midLng },
      { lat: ne.lat(), lng: ne.lng() },
    ),
    new google.maps.LatLngBounds(
      { lat: sw.lat(), lng: sw.lng() },
      { lat: midLat, lng: midLng },
    ),
    new google.maps.LatLngBounds(
      { lat: sw.lat(), lng: midLng },
      { lat: midLat, lng: ne.lng() },
    ),
  ];
};

export const searchQuadrants = async (
  Place: typeof google.maps.places.Place,
  textQuery: string,
  bounds: google.maps.LatLngBounds,
) => {
  const quadrants = splitBoundsIntoQuadrants(bounds);

  const results = await Promise.all(
    quadrants.map((quadrant) =>
      Place.searchByText({
        textQuery,
        locationRestriction: quadrant,
        maxResultCount: 20,
        fields: PLACE_FIELDS,
      }).catch(() => ({ places: [] as google.maps.places.Place[] })),
    ),
  );

  const allPlaces = results.flatMap((r) => r.places);
  const seen = new Set<string>();
  return allPlaces.filter((place) => {
    if (!place.id || seen.has(place.id)) return false;
    seen.add(place.id);
    return true;
  });
};

const PlacesSearchPage = () => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { profile } = useContext(OrganizationUserContext);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [places, setPlaces] = useState<GooglePlaceResult[] | null>(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [addedPlaces, setAddedPlaces] = useState<GooglePlaceResult[]>([]);
  const [googlePlaceBrands, setGooglePlaceBrands] = useState<
    GooglePlaceBrand[]
  >([]);
  const [checkoutOpen, setCheckoutOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [brandVerificationOpen, setBrandVerificationOpen] =
    useState<boolean>(false);
  const [builderCampaignId, setBuilderCampaignId] = useState<number | null>(
    null,
  );
  const { hasBanner } = usePageSize();

  const itemsRef = useRef<Record<string, HTMLDivElement | null>>({});

  const mapRef = useRef<google.maps.Map | null>(null);
  const { emailHealth, setOpenIntegrationDialog } = useContext(
    UserIntegrationsContext,
  );
  const navigate = useNavigate();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (profile && profile.latitude && profile.longitude) {
      setMapCenter({ lat: profile.latitude, lng: profile.longitude });
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter({ lat: latitude, lng: longitude });
      },
      () => {
        if (!profile || !profile.latitude || !profile.longitude) {
          setLocationDialogOpen(true);
        }
      },
    );

    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const searchThisArea = async (e: CustomEvent) => {
    e.preventDefault();
    if (!window.google || !inputValue) return;

    setLoading(true);
    setPlaces([]);
    setSearchQuery(inputValue);

    const { Place } = (await google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    const map = mapRef.current;

    trackEvent("Local Pitching Search This Area Button Pressed", {
      searchQuery: inputValue,
      latitude: map?.getCenter()?.lat(),
      longitude: map?.getCenter()?.lng(),
    });

    const bounds = map?.getBounds();
    if (!bounds) return;

    try {
      const allPlaces = await searchQuadrants(Place, inputValue, bounds);

      const filteredPlaces = allPlaces.filter(
        (place) => place.businessStatus === "OPERATIONAL" && !!place.websiteURI,
      );

      setPlaces(filteredPlaces as GooglePlaceResult[]);
      setSelectedPlaceId(null);
    } catch (err) {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  };

  const performTextSearch = async (e: CustomEvent) => {
    e.preventDefault();
    if (!window.google || !inputValue) return;

    setLoading(true);
    setPlaces([]);
    setSearchQuery(inputValue);

    const { Place } = (await google.maps.importLibrary(
      "places",
    )) as google.maps.PlacesLibrary;

    const map = mapRef.current;
    const center = map?.getCenter();
    if (!center) return;

    trackEvent("Local Pitching Search Submitted", {
      searchQuery: inputValue,
      latitude: map?.getCenter()?.lat(),
      longitude: map?.getCenter()?.lng(),
    });

    const bounds = map?.getBounds();
    if (!bounds) return;

    try {
      const allPlaces = await searchQuadrants(Place, inputValue, bounds);

      const filteredPlaces = allPlaces.filter(
        (place) => place.businessStatus === "OPERATIONAL" && !!place.websiteURI,
      );

      setPlaces(filteredPlaces as GooglePlaceResult[]);
      setSelectedPlaceId(null);

      if (filteredPlaces && filteredPlaces.length > 0) {
        const { LatLngBounds } = (await google.maps.importLibrary(
          "core",
        )) as google.maps.CoreLibrary;
        const fitBounds = new LatLngBounds();

        filteredPlaces.forEach((place) => {
          fitBounds.extend(place.location as google.maps.LatLng);
        });

        map?.fitBounds(fitBounds);
      }
    } catch (err) {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = (placeId?: string) => {
    if (placeId) {
      setSelectedPlaceId(placeId);
      const el = itemsRef.current[placeId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const placeClicked = (place: GooglePlaceResult) => {
    const loc = place.location;
    if (loc) mapRef.current?.panTo(loc);
    setSelectedPlaceId(place.id);
  };

  const addPlace = (place: GooglePlaceResult) => {
    setAddedPlaces((prev) => {
      const exists = prev.some((p) => p.id === place.id);
      if (exists) {
        // Remove it
        trackEvent("Local Pitching Place Removed", {
          "Place Id": place.id,
          "Place Name": place.displayName,
        });
        return prev.filter((p) => p.id !== place.id);
      } else {
        // Add it
        trackEvent("Local Pitching Place Added", {
          "Place Id": place.id,
          "Place Name": place.displayName,
        });
        return [...prev, place];
      }
    });
  };

  if (loadError) {
    return <div>Error loading Google Maps. Please try again.</div>;
  }

  if (!isLoaded) {
    return <div>Loading Maps View...</div>;
  }

  if (!currentOrg?.isPaidUser) {
    return <LocalPitchingPreviewPage />;
  }

  return (
    <>
      {" "}
      <Box
        sx={{
          height: {
            xs: hasBanner ? "calc(100vh - 180px)" : "calc(100vh - 110px)",
            md: hasBanner ? "calc(100vh - 120px)" : "calc(100vh - 80px)",
          },
          overflow: "hidden",
        }}
      >
        <Grid container sx={{ height: "100%" }}>
          <Grid
            item
            xs={12}
            md={6}
            lg={5}
            xl={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #ccc",
              height: { xs: "60%", md: "100%" },
              py: 2,
            }}
          >
            <form onSubmit={performTextSearch}>
              <Grid container sx={{ px: 2, mb: 2 }} gap={1}>
                <Grid item xs>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    placeholder="Tacos in Mountain View"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                    }}
                    sx={{ flexShrink: 0 }}
                  />
                </Grid>
                <Grid item xs="auto">
                  <Button
                    type="submit"
                    sx={{ height: 37.125 }}
                    variant="contained"
                    disableElevation
                  >
                    <Box
                      component="i"
                      className="fa-solid fa-magnifying-glass"
                    />
                  </Button>
                </Grid>
              </Grid>
            </form>
            <Box
              sx={{
                overflowY: "auto",
                flexGrow: 1,
              }}
            >
              {places === null && !loading && <OnboardingLocal />}
              {!loading && places !== null && places.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pt: 2,
                  }}
                >
                  <Typography
                    color="textSecondary"
                    sx={{ maxWidth: "300px" }}
                    textAlign="center"
                  >
                    No results found. Try changing the area you are searching or
                    the search query.
                  </Typography>
                </Box>
              )}
              {loading && (
                <Grid container rowGap={1} sx={{ pb: 1 }}>
                  {Array.from({ length: 5 }, (x, i) => (
                    <Grid item key={i} xs={12} sx={{ px: 2 }}>
                      <Skeleton
                        variant="rounded"
                        sx={{
                          height: "150px",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              {places?.map((place) => (
                <Box
                  key={place.id}
                  ref={(el) => {
                    if (place.id) {
                      itemsRef.current[place.id] = el as HTMLDivElement | null;
                    }
                  }}
                  sx={{ px: 2 }}
                >
                  <MapListItem
                    place={place}
                    isSelected={selectedPlaceId === place.id}
                    onClick={placeClicked}
                    addPlace={addPlace}
                    isAdded={addedPlaces.some((p) => p.id === place.id)}
                  />
                </Box>
              ))}
            </Box>
            <Box>
              <Grid container sx={{ px: 2, pt: 1 }} gap={1} alignItems="center">
                <Grid item xs>
                  <Button
                    disabled={addedPlaces.length === 0}
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      trackEvent("Local Pitching Pitch to Brands Pressed", {
                        "Number of Added Brands": addedPlaces.length,
                      });
                      if (!emailHealth) {
                        setOpenIntegrationDialog(true);
                        return;
                      }
                      setCheckoutOpen(true);
                    }}
                  >
                    Pitch to brands
                  </Button>
                </Grid>
                <Grid item xs="auto">
                  <Badge
                    badgeContent={addedPlaces.length}
                    sx={{
                      minWidth: "16px",
                      minHeight: "16px",
                      fontSize: "0.6rem",
                    }}
                    color="primary"
                  >
                    <Box
                      component="i"
                      className="fa solid fa-cart-shopping"
                      fontSize="18px"
                      sx={{
                        color: grey[500],
                      }}
                    />
                  </Badge>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={7}
            xl={8}
            sx={{ height: { xs: "40%", md: "100%" }, position: "relative" }}
          >
            <Box sx={{ height: "100%" }}>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                options={mapOptions}
                zoom={14}
                onLoad={onLoad}
              >
                {places?.map(
                  (place, index) =>
                    place.location && (
                      <Marker
                        key={index}
                        position={{
                          lat: place.location.lat(),
                          lng: place.location.lng(),
                        }}
                        onClick={() => handleMarkerClick(place.id)}
                        icon={
                          place.id === selectedPlaceId
                            ? {
                                url:
                                  "data:image/svg+xml;charset=UTF-8," +
                                  encodeURIComponent(`
                                    <svg width="24" height="36" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M12 0C6.5 0 2 4.5 2 10C2 17 12 36 12 36C12 36 22 17 22 10C22 4.5 17.5 0 12 0Z" fill="#014339"/>
                                      <circle cx="12" cy="12" r="4" fill="white"/>
                                    </svg>
                                  `),
                                scaledSize: new google.maps.Size(24, 36),
                                anchor: new google.maps.Point(12, 36),
                              }
                            : {
                                url:
                                  "data:image/svg+xml;charset=UTF-8," +
                                  encodeURIComponent(`
                                  <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="8" fill="${red[800]}" stroke="white" stroke-width="2" />
                                  </svg>
                                `),
                                scaledSize: new google.maps.Size(20, 20),
                                anchor: new google.maps.Point(10, 10),
                              }
                        }
                      />
                    ),
                )}
              </GoogleMap>
              {inputValue && (
                <Button
                  variant="contained"
                  color="inherit"
                  size="small"
                  onClick={searchThisArea}
                  sx={{
                    backgroundColor: "white",
                    position: "absolute",
                    top: "15px",
                    left: "50%",
                    ml: "-90px",
                    width: "180px",
                    textTransform: "none",
                  }}
                >
                  <Box
                    component="span"
                    className="fa fa-magnifying-glass"
                    sx={{ fontSize: 16, mr: 1 }}
                  />{" "}
                  Search this area
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {brandVerificationOpen && (
        <BrandVerificationDialog
          googlePlaceBrands={googlePlaceBrands}
          open={brandVerificationOpen}
          searchQuery={searchQuery}
          handleClose={() => setBrandVerificationOpen(false)}
          setGooglePlaceBrands={setGooglePlaceBrands}
          handleBack={() => {
            setBrandVerificationOpen(false);
            setCheckoutOpen(true);
          }}
          openCompleted={(campaignId: number) => {
            setInputValue("");
            setSelectedPlaceId(null);
            setAddedPlaces([]);
            setPlaces(null);
            setBuilderCampaignId(campaignId);
          }}
        />
      )}
      {checkoutOpen && (
        <CheckoutDialog
          open={checkoutOpen}
          handleClose={() => setCheckoutOpen(false)}
          addedPlaces={addedPlaces}
          addPlace={addPlace}
          openBrandVerificationDialog={() => setBrandVerificationOpen(true)}
          setGooglePlaceBrands={setGooglePlaceBrands}
        />
      )}
      {builderCampaignId && (
        <CampaignBuilderDialog
          open={!!builderCampaignId}
          campaignId={builderCampaignId}
          initialStep={CampaignStep.write_email}
          onClose={() => setBuilderCampaignId(null)}
        />
      )}
      {locationDialogOpen && (
        <LocationDialog
          open={locationDialogOpen}
          handleClose={(latitude?: number, longitude?: number) => {
            setLocationDialogOpen(false);
            if (latitude && longitude) {
              setMapCenter({ lat: latitude, lng: longitude });
            }
          }}
        />
      )}
      <Routes>
        <Route
          path="success"
          element={
            <CompletedDialog
              open={true}
              searchQuery={searchQuery}
              handleClose={() => {
                navigate(".");
              }}
            />
          }
        />
      </Routes>
    </>
  );
};

export default PlacesSearchPage;
